import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { initializeTestAccounts } from "../utils/initializeAccounts";
import authService from "../services/authService";
import { ENV, isMockDataEnabled } from "../config/env";
import { debugServerGroupCall } from "../services/debugAuth";
import {
  saveLoginInfo,
  getSavedLoginInfo,
  clearSavedLoginInfo,
} from "../utils/cookieUtils";
import "./Login.css";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [centerId, setCenterId] = useState("");
  const [loginId, setLoginId] = useState(""); // Can be userid or email
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [serverInfo, setServerInfo] = useState<string>("");
  // const [envStatus, setEnvStatus] = useState<{
  //   hasKeys: boolean;
  //   mode: string;
  // }>({ hasKeys: false, mode: "" });

  useEffect(() => {
    // Check environment on component mount
    const hasAccessKey = !!ENV.ACCESS_KEY;
    const hasActionKey = !!ENV.ACTION_KEY;
    const hasKeys = hasAccessKey && hasActionKey;
    const mode = isMockDataEnabled() ? "Mock 모드" : "실제 API 모드";
    // setEnvStatus({ hasKeys, mode });

    // 저장된 로그인 정보 불러오기
    const savedInfo = getSavedLoginInfo();
    if (savedInfo) {
      setCenterId(savedInfo.centerId);
      setLoginId(savedInfo.loginId);
      setRememberMe(true);
      // 센터ID가 있으면 서버 정보도 설정
      // if (savedInfo.centerId.length >= 3) {
      //   const centerInfo = authService.getCenterInfo(savedInfo.centerId);
      //   if (centerInfo) {
      //     setServerInfo(
      //       `서버 그룹: ${centerInfo.serverGroup} (${centerInfo.centerName})`
      //     );
      //   }
      // }
    }

    console.log("Environment check:", {
      ACCESS_KEY: hasAccessKey
        ? `Set (${ENV.ACCESS_KEY.length} chars)`
        : "NOT SET",
      ACTION_KEY: hasActionKey
        ? `Set (${ENV.ACTION_KEY.length} chars)`
        : "NOT SET",
      hasKeys,
      mode,
    });

    if (!hasKeys) {
      console.error("⚠️ Environment keys are missing!");
      console.error("ACCESS_KEY:", hasAccessKey ? "OK" : "MISSING");
      console.error("ACTION_KEY:", hasActionKey ? "OK" : "MISSING");
    }

    // Make debug function available globally
    (window as any).debugServerGroupCall = debugServerGroupCall;
    (window as any).ENV = ENV;
    console.log(
      '🔧 Debug: Run debugServerGroupCall("test.mytro") in console to test API'
    );
    console.log("💡 Check ENV object in console to see environment variables");
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!centerId || !loginId || !password) {
      setError("모든 필드를 입력해주세요.");
      setLoading(false);
      return;
    }

    const result = await login(centerId, loginId, password);

    if (result.success) {
      // 로그인 성공 시 아이디 저장/삭제 처리
      if (rememberMe) {
        saveLoginInfo(centerId, loginId);
      } else {
        clearSavedLoginInfo();
      }
      navigate("/");
    } else {
      setError(result.message || "로그인에 실패했습니다.");
    }

    setLoading(false);
  };

  const handleCenterIdChange = async (value: string) => {
    setCenterId(value);
    setServerInfo("");

    // if (value.length >= 3) {
    //   const centerInfo = authService.getCenterInfo(value);
    //   if (centerInfo) {
    //     setServerInfo(
    //       `서버 그룹: ${centerInfo.serverGroup} (${centerInfo.centerName})`
    //     );
    //   }
    // }
  };

  // const handleResetAccounts = () => {
  //   localStorage.removeItem("users");
  //   localStorage.removeItem("user");
  //   initializeTestAccounts();
  //   setError("");
  //   alert("테스트 계정이 초기화되었습니다. 이제 로그인할 수 있습니다.");
  // };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <img
              src="/Matrixcloud_logo.png"
              alt="Matrixcloud Logo"
              className="logo-image"
            />
          </div>
          <h1>Matrixcloud</h1>
          <p className="auth-subtitle">로그인</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="centerId">센터 ID</label>
            <input
              type="text"
              id="centerId"
              value={centerId}
              onChange={(e) => handleCenterIdChange(e.target.value)}
              placeholder="센터 ID 입력"
              disabled={loading}
              autoComplete="organization"
            />
            {serverInfo && <small className="server-info">{serverInfo}</small>}
          </div>

          <div className="form-group">
            <label htmlFor="loginId">아이디</label>
            <input
              type="text"
              id="loginId"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              placeholder="아이디를 입력하세요"
              disabled={loading}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">비밀번호</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호를 입력하세요"
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          <div className="form-group remember-me-group">
            <label className="remember-me-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
                className="remember-me-checkbox"
              />
              <span className="checkmark"></span>
              아이디 저장
            </label>
          </div>

          {/* 버튼 텍스트를 가운데 정렬*/}
          <button
            type="submit"
            className="btn btn-primary btn-block mt-4"
            disabled={loading}
          >
            {loading ? "로그인 중..." : "로그인"}
          </button>
        </form>
        {/*
        <div className="auth-footer">
          <p>
            계정이 없으신가요? <Link to="/signup">회원가입</Link>
          </p>
          <button
            type="button"
            className="btn btn-secondary btn-block"
            onClick={handleResetAccounts}
            style={{ marginTop: "10px" }}
          >
            테스트 계정 초기화
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-block"
            onClick={() => {
              // const hasKeys = validateEnvironment();
              const mode = isMockDataEnabled() ? "Mock 모드" : "실제 API 모드";
              // setEnvStatus({ hasKeys, mode });
              const hasAccessKey = !!ENV.ACCESS_KEY;
              const hasActionKey = !!ENV.ACTION_KEY;
              alert(
                `환경 설정 확인\n\nACCESS_KEY: ${
                  hasAccessKey ? "설정됨" : "누락"
                }\nACTION_KEY: ${
                  hasActionKey ? "설정됨" : "누락"
                }\n모드: ${mode}`
              );
            }}
            style={{
              marginTop: "5px",
              backgroundColor: !envStatus.hasKeys ? "#dc3545" : "#28a745",
            }}
          >
            {!envStatus.hasKeys ? "⚠️ 환경 변수 확인" : "✓ 환경 설정 확인"}
          </button>
        </div>
        */}
      </div>
    </div>
  );
};

export default Login;
